import React, { useEffect, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useSnackbar } from '../../../components/SnackbarProvider';
import TourLayoutContainer from '../../../components/vanliferComponents/TourContainer';
import TourSidebar from '../../../components/vanliferComponents/TourSidebar';
import TourSidebarContent from "../../../components/vanliferComponents/Community/TourSidebarContent";
import TourOverviewCard from '../../../components/vanliferComponents/Community/TourOverviewCard';
import StoriesView from '../../../components/vanliferComponents/Community/StoriesView/StoriesView';
import EditTourDetailsDialog from '../../../components/vanliferComponents/Community/Dialogs/EditTourDetailsDialog';
import EditSharingLinkDialog from "../../../components/vanliferComponents/Community/Dialogs/EditSharingLinkDialog";
import { useAppDispatch, useAppSelector } from '../../../hooks/hooks';
import { asyncModifyTourCommunity, loadTourDetail } from '../../../store/Vanlifer/tourSlice';
import { TourDetail } from "../../../services/restserver-openapi";
import sharingMode = TourDetail.sharingMode;
import TourItinerary from "../TourPage/TourItinerary";

export default function CommunityTourPage() {
    const { showSnackbar } = useSnackbar();
    const { tourId } = useParams();
    const [searchParams] = useSearchParams();
    const accessKey = searchParams.get('key');
    const navigate = useNavigate();
    const dispatch = useAppDispatch();
    const isAuthenticated = useAppSelector(state => state.login.isAuthenticated);

    // Check whether to fetch tour details using access token only (unauthenticated access) or also include credentials (authenticated)
    const tokenInSession = sessionStorage.getItem("token");

    const chosenTourDetail = useAppSelector(state => state.tour.choosedTourDetail);

    const parsedTourId = parseInt(tourId!);

    useEffect(() => {
        const fetchTourDetail = async () => {
            if (!tourId) {
                navigate('/Vanlifer/BookingOverview');
                return;
            }

            const id: number = parseInt(tourId);
            if (isNaN(id)) {
                console.warn("Invalid tourId");
                navigate('/Vanlifer/BookingOverview');
                return;
            }

            try {
                await dispatch(loadTourDetail({tourId: id, accessKey: accessKey ?? undefined})).unwrap();
            } catch (err: any) {
                console.error("Failed to load tour detail:", err?.message || err);
                navigate('/Vanlifer/BookingOverview');
            }
        };

        if (isAuthenticated || !tokenInSession) {
            fetchTourDetail();
        }
    }, [dispatch, navigate, tourId, isAuthenticated]);

    const [editOpen, setEditOpen] = useState(false);
    const [editLinkOpen, setEditLinkOpen] = useState(false);

    const handleEditSave = async (data: {
        name: string;
        description: string;
        sharingMode: TourDetail.sharingMode;
        hasNewImage: boolean;
        imageName?: string;
        fileExtension?: string;
    }) => {
        if (!tourId || !chosenTourDetail) return;

        const updatedTour = {
            tourId: chosenTourDetail.tourId!,
            name: data.name,
            description: data.description,
            sharingMode: data.sharingMode,
            ...(data.hasNewImage && data.imageName !== undefined
                    ? {
                        imageName: data.imageName,
                        fileExtension: data.fileExtension,
                    }
                    : data.hasNewImage
                        ? {
                            imageName: "delete", // tell backend to get rid of old image
                            fileExtension: "",
                        }
                        : {} // keep existing image
            ),
        };


        try {
            await dispatch(asyncModifyTourCommunity(updatedTour)).unwrap();
            await dispatch(loadTourDetail({ tourId: updatedTour.tourId, accessKey: accessKey ?? undefined })).unwrap();
            showSnackbar('Reise erfolgreich aktualisiert', 'success');
            setEditOpen(false);
        } catch (error: any) {
            showSnackbar('Fehler beim Speichern der Reise', 'error');
        }
    };

    return (
        <>
            <TourLayoutContainer
                Sidebar={
                    <TourSidebar>
                        <TourSidebarContent
                            onEditClick={() => setEditOpen(true)}
                            onEditLinkClick={() => setEditLinkOpen(true)}
                            showSnackbar={showSnackbar}
                        />
                    </TourSidebar>
                }
                tabs={[
                    {
                        label: 'Tourverlauf',
                        content: <TourItinerary defaultEditingEnabled={false} showEditingToggle={false} /> },
                    {
                        label: 'Stories',
                        content: (
                            <StoriesView
                                tourId={parsedTourId}
                                chosenTourDetail={chosenTourDetail}
                                showSnackbar={showSnackbar}
                                accessKey={accessKey ?? undefined}
                            />
                        ),
                    },
                ]}
                floatingComponents={
                    <>
                        <EditTourDetailsDialog
                            open={editOpen}
                            onClose={() => setEditOpen(false)}
                            onSave={handleEditSave}
                            tourId={chosenTourDetail.tourId}
                            initialTitle={chosenTourDetail.name}
                            initialDescription={chosenTourDetail.description}
                            initialSharingMode={chosenTourDetail.sharingMode}
                            initialSasLink={chosenTourDetail.sasDownloadLink}
                        />
                        <EditSharingLinkDialog
                            open={editLinkOpen}
                            tourId={chosenTourDetail.tourId!}
                            isPrivate={chosenTourDetail.sharingMode === sharingMode.PRIVATE}
                            onClose={() => setEditLinkOpen(false)}
                            showSnackbar={showSnackbar}
                        />
                    </>
                }
            />
        </>
    );
}

